import { ValidationError } from "./error-utils.js";

/**
 * Waits for a video transcoding process to complete by polling a video API until the video is published or a timeout is reached.
 *
 * @param {string} videoId - The unique identifier of the video to monitor.
 * @param {string} token - The authorization token for API requests.
 * @param {function} onProgress - A callback function invoked with a progress percentage indicating the polling progress.
 * @param timeoutRef
 * @returns {Promise<object>} A promise that resolves with the video object when transcoding is complete or rejects if an error occurs or the maximum polling attempts are exceeded.
 * @throws {Error} Throws an error if transcoding does not complete within the allowed time or if an API request fails.
 */
const waitForTranscodingToFinish = async (
  videoId,
  token,
  onProgress,
  timeoutRef,
) => {
  const POLLING_INTERVAL = 5000; // 5 seconds
  const MAX_ATTEMPTS = 20; // Stop polling after 20 attempts (100 seconds)
  let attempts = 0;

  return new Promise((resolve, reject) => {
    const poll = async () => {
      attempts++;

      try {
        const response = await fetch(
          `${import.meta.env.VITE_VIDEO_UPLOAD_HOST}/api/v1/videos/${videoId}`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );

        const video = await response.json();

        if (video.state.id === 1) {
          // 1 === published; meaning transcoding is done since we wait with publishing until transcoding is done
          if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
          }
          onProgress(100);
          resolve(video);
        } else if (attempts >= MAX_ATTEMPTS) {
          // Transcoding timed out
          if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
          }
          reject(
            new Error("Transcoding did not complete within the expected time."),
          );
        } else {
          // Continue polling
          onProgress(Math.floor((attempts / MAX_ATTEMPTS) * 100)); // Tell the user that we are working on it.
          // Clear existing timeout and track new one
          if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
          }
          timeoutRef.current = setTimeout(poll, POLLING_INTERVAL);
        }
      } catch (error) {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
          timeoutRef.current = null;
        }
        reject(error);
      }
    };

    poll();
  });
};

/**
 * Asynchronously uploads a video to the peertube server while reporting on the progress.
 *
 * @param {Object} params - The parameters for the upload process.
 * @param {File} params.file - The file to be uploaded.
 * @param {Function} params.onProgress - Callback function invoked with the upload progress percentage.
 * @param {string} params.token - The authorization token for the upload.
 * @param {string} params.channelId - The ID of the channel where the file will be uploaded.
 *
 * @returns {Promise<Object>} - A promise that resolves with the server response when the upload is complete.
 */
const uploadFile = async ({
  file,
  onProgress,
  token,
  channelId,
  timeoutRef,
}) => {
  const data = new FormData();
  data.append("name", file.name || "video");
  data.append("privacy", 3); // private
  data.append("nsfw", false);
  data.append("commentsEnabled", false);
  data.append("downloadEnabled", false);
  data.append("waitTranscoding", true);
  data.append("channelId", channelId);
  data.append("videofile", file);

  const request = new XMLHttpRequest();
  request.open(
    "POST",
    `${import.meta.env.VITE_VIDEO_UPLOAD_HOST}/api/v1/videos/upload`,
  );
  request.setRequestHeader("authorization", `Bearer ${token}`);

  return new Promise((resolve, reject) => {
    // Store references to handlers for cleanup
    const progressHandler = function (e) {
      const completed = Math.floor((e.loaded / e.total) * 100);
      onProgress(completed);
    };

    const loadHandler = async function () {
      cleanup(); // Clean up listeners before processing
      if (request.status < 400) {
        const loadRes = JSON.parse(request.response);

        try {
          await waitForTranscodingToFinish(
            loadRes.video.id,
            token,
            onProgress,
            timeoutRef,
          );
          resolve(loadRes); // Resolve only after transcoding is complete
        } catch (err) {
          reject(err); // Reject if transcoding fails/times out
        }
      } else {
        reject({
          message: `Failed video upload with status ${request.status}`,
          response: request.response,
        });
      }
    };

    const errorHandler = function (e) {
      cleanup(); // Clean up listeners before rejecting
      reject(e);
    };

    // Function to clean up all listeners
    const cleanup = () => {
      request.upload.removeEventListener("progress", progressHandler);
      request.removeEventListener("load", loadHandler);
      request.removeEventListener("error", errorHandler);
    };

    // Add event listeners
    request.upload.addEventListener("progress", progressHandler);
    request.addEventListener("load", loadHandler);
    request.addEventListener("error", errorHandler);

    request.send(data);
  });
};

/**
 * Asynchronously uploads a video file to a specified channel.
 *
 * @param {Object} params - Object containing parameters for video upload.
 * @param {File} params.file - The video file to be uploaded.
 * @param {string} params.key - The form field the file belongs to, named in validation errors.
 * @param {string} params.token - Authentication token for authorization.
 * @param {string} params.channelId - The ID of the channel where the video will be uploaded.
 * @param {Function} params.onProgress - Callback function invoked with the upload progress percentage.
 * @param {Function} params.onUploaded - Callback function to execute after a successful upload, receiving the result as an argument.
 * @param {Function} [params.onError] - Optional callback function to execute in case of an error during the upload process, receiving the error as an argument.
 * @param {{ current: ReturnType<typeof setTimeout> | null }} params.timeoutRef - Holds the transcoding poll's pending timeout, so the caller can clear it on unmount.
 * @returns {Promise<void>} A promise that resolves when the upload completes successfully or rejects if an error occurs.
 */
export const uploadVideo = async ({
  file,
  key,
  token,
  channelId,
  onProgress,
  onUploaded,
  onError,
  timeoutRef,
}) => {
  let result;
  try {
    validateVideoFile(file, key);
    result = await uploadFile({
      file,
      onProgress,
      token,
      channelId,
      timeoutRef,
    });
  } catch (e) {
    // Make sure to clear timeout on error
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    if (onError) {
      onError(e);
    } else {
      return Promise.reject(e);
    }
  }

  if (result) onUploaded(result);
};

/**
 * Validates a video file based on its MIME type and size.
 *
 * This function checks whether the provided file is a valid video by testing its MIME type against a predetermined pattern.
 * It also validates that the file size does not exceed 25MB.
 *
 */
const validateVideoFile = (file, fieldName) => {
  const videoTypePattern = /^video\/.+$/;
  const maxSize = 25 * 1024 * 1024; // 25MB

  if (!videoTypePattern.test(file.type)) {
    throw new ValidationError({
      message:
        "Dieses Dateiformat kann nicht gelesen werden. Bitte uploade mit diesem Button nur Video-Dateien.",
      field: fieldName,
    });
  }

  if (file.size > maxSize) {
    throw new ValidationError({
      message:
        "Dieses Video ist zu groß. Bitte editiere dein Video, sodass es maximal 25MB groß ist.",
      field: fieldName,
    });
  }
};
